/**
 * ACTIVITY-CORE — human-readable feed/history text (i18n).
 *
 * Chronos-SPECIFIC part: the entity_type+action → phrase map. The structure
 * (patch, grouping, offline) lives in @time-tracker/shared. It returns
 * `msg` descriptors (not strings) so the language is reactive.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@time-tracker/shared";

interface ActivitySentence {
  /** Sentence with `{name}` interpolated. */
  named: MessageDescriptor;
  /** Sentence without a name (the patch does not contain one). */
  anonymous: MessageDescriptor;
}

/**
 * Fine-grained semantic key: refines `updated`/`deleted` with what actually happened
 * (stopping the timer, moving to trash…) by looking at the patch.
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "time_entry" && action === "updated" && patch.end_time) {
    const wasRunning = patch.end_time.old === null || patch.end_time.old === undefined;
    if (wasRunning && patch.end_time.new) {
      return "stopped";
    }
  }
  if (action === "deleted") {
    // deleted_at transition = trash; full snapshot = permanent purge.
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

const ENTRY_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`logged time on "{name}"`, anonymous: msg`logged a time block` },
  stopped: { named: msg`stopped the timer on "{name}"`, anonymous: msg`stopped a timer` },
  updated: { named: msg`edited the time block "{name}"`, anonymous: msg`edited a time block` },
  trashed: {
    named: msg`moved the time block "{name}" to the trash`,
    anonymous: msg`moved a time block to the trash`,
  },
  purged: {
    named: msg`permanently deleted the time block "{name}"`,
    anonymous: msg`permanently deleted a time block`,
  },
  restored: {
    named: msg`restored the time block "{name}"`,
    anonymous: msg`restored a time block`,
  },
  reverted: {
    named: msg`undid a change on the time block "{name}"`,
    anonymous: msg`undid a change on a time block`,
  },
};

const PROJECT_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the project "{name}"`, anonymous: msg`created a project` },
  updated: { named: msg`edited the project "{name}"`, anonymous: msg`edited a project` },
  trashed: {
    named: msg`moved the project "{name}" to the trash`,
    anonymous: msg`moved a project to the trash`,
  },
  purged: {
    named: msg`permanently deleted the project "{name}"`,
    anonymous: msg`permanently deleted a project`,
  },
  restored: { named: msg`restored the project "{name}"`, anonymous: msg`restored a project` },
  reverted: {
    named: msg`undid a change on the project "{name}"`,
    anonymous: msg`undid a change on a project`,
  },
};

const WORKSPACE_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the workspace "{name}"`, anonymous: msg`created a workspace` },
  updated: { named: msg`edited the workspace "{name}"`, anonymous: msg`edited a workspace` },
  trashed: {
    named: msg`moved the workspace "{name}" to the trash`,
    anonymous: msg`moved a workspace to the trash`,
  },
  restored: { named: msg`restored the workspace "{name}"`, anonymous: msg`restored a workspace` },
  reverted: {
    named: msg`undid a change on the workspace "{name}"`,
    anonymous: msg`undid a change on a workspace`,
  },
};

// Memberships have no human-readable "name" → always use an anonymous sentence.
const MEMBER_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added a member`, anonymous: msg`added a member` },
  updated: { named: msg`changed a member's role`, anonymous: msg`changed a member's role` },
  trashed: { named: msg`removed a member`, anonymous: msg`removed a member` },
  restored: { named: msg`re-added a member`, anonymous: msg`re-added a member` },
  reverted: { named: msg`undid a membership change`, anonymous: msg`undid a membership change` },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

/** Visible entity name extracted from the patch (description/name, new or old). */
export function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["description", "name"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/**
 * i18n sentence for an entry: descriptor + values. Render with
 * `t(descriptor, values)` in the component.
 */
export function activitySentence(row: ActivityRow): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const table =
    row.entity_type === "time_entry"
      ? ENTRY_TEXTS
      : row.entity_type === "project"
        ? PROJECT_TEXTS
        : row.entity_type === "workspace"
          ? WORKSPACE_TEXTS
          : row.entity_type === "workspace_member"
            ? MEMBER_TEXTS
            : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Localized "5 minutes ago" with Intl; falls back to day/date for older items. */
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < 7 * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
